/**
 * Monthly salary — the numerator of the expense ceiling.
 *
 * Versioned by `effectiveFrom` for the same reason budgets are: a raise in July
 * must not retroactively change what June's ceiling was. The period it applies
 * to is **derived** from that date, never stored (INV-2).
 *
 * This is deliberately one figure and not an income ledger. There is no payslip,
 * no second source, and no history to browse — the moment it grows those it has
 * exceeded the 2026-07-31 scope expansion that admitted it.
 */

import type { Cents } from "./money";
import { versionInForce } from "./versions";
import { type Period } from "./period";

export interface SalaryRow {
  readonly amountCents: Cents;
  /** `YYYY-MM-DD`. */
  readonly effectiveFrom: string;
}

/**
 * The salary in force for a period: the latest version whose `effectiveFrom` is
 * on or before the period's first day.
 *
 * Returns null when no salary has ever been set, and null must not collapse to
 * zero — the same distinction {@link baseBudget} draws. Zero is a claim that the
 * user earns nothing, which would show every expense as over the ceiling; null
 * means the ceiling is unknown, which shows no ceiling at all.
 */
export function salaryInForce(
  salaries: readonly SalaryRow[],
  period: Period,
  timeZone: string,
): Cents | null {
  return versionInForce(salaries, period, timeZone)?.amountCents ?? null;
}
